import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, DotDotDotVerticalIcon, MutedSpeakerIcon, StreamrNewLogoSolo, UnmutedSpeakerIcon, } from '@streamr/tv-ui';
import { useRef } from 'react';
import styled from 'styled-components';
import { MediaService } from '../../services/media/MediaService';
import { StreamrAppService } from '../../services/streamrapp/StreamrAppService';
import { Anchor } from '../Anchor';
import { RecordingStatusIndicator } from '../broadcaster/helpers/RecordingStatusIndicator';
import { ToggleLiveButton } from '../broadcaster/helpers/ToggleLiveButton';
import { IncomingRequestsButton } from '../broadcaster/IncomingRequestsButton';
import { AudioDeviceButton, VideoDeviceButton } from '../DeviceButton';
import { LiveIndicatorWithCount } from '../LiveIndicator';
import { showBroadcastPopup } from '../popups/BroadcastPopup';
import { selectCamera } from '../popups/devices/CameraSelectorPopup';
import { selectMicrophone } from '../popups/devices/MicrophoneSelectorPopup';
import { ShareButtonRound } from '../ShareButtons';
import { BottomLeftSlot, BottomRightSlot, HudSlots, TopLeftSlot, TopRightSlot, } from '../shared/HudSlots';
import { LogoLink } from '../shared/LogoLink';
import { MobilePluginButtonTray } from '../shared/PluginButtonTray';
export function StreamrAppSpeakerViewMobileHud({ activeBroadcast, broadcastUrl, fullscreenButton, visible, isBroadcaster, isBroadcasting, onGoLiveClick, onEndBroadcastClick, }) {
    const settingsButtonAnchorRef = useRef(null);
    const broadcastOutputReady = StreamrAppService.getOutput().useReady();
    const mediaService = MediaService.getNotificationInterface();
    const mediaOutputAudioMuted = MediaService.getOutput().useAudioMuted();
    const mediaOutputVideoMuted = MediaService.getOutput().useVideoMuted();
    const connectedVideoDevices = MediaService.getOutput().useConnectedVideoDevices();
    const connectedAudioDevices = MediaService.getOutput().useConnectedAudioDevices();
    const playbackAudioMuted = !!StreamrAppService.getOutput().useAudioMuted();
    const onMuteAudioButtonClicked = () => {
        if (mediaOutputAudioMuted) {
            mediaService.notifyAudioMutingChangeRequested(false);
        }
        else {
            mediaService.notifyAudioMutingChangeRequested(true);
        }
    };
    const onMuteVideoButtonClicked = () => {
        if (mediaOutputVideoMuted) {
            mediaService.notifyVideoMutingChangeRequested(false);
        }
        else {
            mediaService.notifyVideoMutingChangeRequested(true);
        }
    };
    return (_jsxs(Hud, { "$visible": visible, children: [_jsx(TopLeftSlot, { children: _jsx(LogoLink, { logoImage: _jsx(StreamrNewLogoSolo, {}) }) }), _jsx(TopRightSlot, { children: isBroadcasting && _jsx(LiveIndicatorWithCount, {}) }), _jsxs(StyledBottomLeftSlot, { children: [isBroadcasting && (_jsx(PlaybackMuteButton, { type: "button", onClick: () => {
                            StreamrAppService.getNotificationInterface().notifyToggleAudio();
                        }, "$isMuted": playbackAudioMuted, children: playbackAudioMuted ? (_jsx(MutedSpeakerIcon, {})) : (_jsx(UnmutedSpeakerIcon, {})) })), _jsx(AudioDeviceButton, { devicePresent: connectedAudioDevices.length > 0, muted: mediaOutputAudioMuted, onMuteClick: onMuteAudioButtonClicked, onSelectClick: (anchor) => {
                            selectMicrophone({
                                anchorRef: anchor,
                                variant: 'top-center',
                            });
                        } }), _jsx(VideoDeviceButton, { devicePresent: connectedVideoDevices.length > 0, muted: mediaOutputVideoMuted, onMuteClick: onMuteVideoButtonClicked, onSelectClick: (anchor) => {
                            selectCamera({
                                anchorRef: anchor,
                                variant: 'top-center',
                            });
                        } }), isBroadcaster && (_jsxs(_Fragment, { children: [_jsx(Anchor, { ref: settingsButtonAnchorRef, children: _jsx(SettingsButton, { type: "button", onClick: () => {
                                        showBroadcastPopup({
                                            broadcast: activeBroadcast,
                                            anchorRef: settingsButtonAnchorRef,
                                            variant: 'mobile',
                                            context: 'multi-broadcaster',
                                        });
                                    }, children: _jsx(DotDotDotVerticalIcon, {}) }) }), _jsx(ToggleLiveButton, { device: "mobile", broadcastServiceIsRunning: broadcastOutputReady, isBroadcasting: isBroadcasting, onGoLiveClick: onGoLiveClick, onEndBroadcastClick: onEndBroadcastClick }), _jsx(IncomingRequestsButton, { variant: "mobile" })] }))] }), _jsxs(StyledBottomRightSlot, { children: [_jsx(RecordingStatusIndicator, { isMobile: true, broadcast: activeBroadcast }), fullscreenButton, _jsx(ShareButtonRound, { url: broadcastUrl }), _jsx(MobilePluginButtonTray, { slotId: "buttonstray" })] })] }));
}
const SettingsButton = styled(Button) `
    --Button_Background: var(--tv-ui-color-12);
    --Button_HoverBackground: var(--tv-ui-color-12);
    --Button_TextColor: var(--tv-ui-color-3);
    --Button_BorderRadius: 1.5rem;
    --Button_Height: 3rem;
    --Button_Padding: 0 1rem;
    backdrop-filter: blur(10px);
`;
const PlaybackMuteButton = styled(Button) `
    --Button_Background: ${({ $isMuted }) => $isMuted ? 'var(--tv-ui-color-3)' : 'var(--tv-ui-color-12)'};
    --Button_HoverBackground: ${({ $isMuted }) => $isMuted ? 'var(--tv-ui-color-3)' : 'var(--tv-ui-color-12)'};
    --Button_TextColor: ${({ $isMuted }) => $isMuted ? 'var(--tv-ui-color-1)' : 'var(--tv-ui-color-3)'};
    --Button_BorderRadius: 1.5rem;
    --Button_Height: 3rem;
    --Button_Width: 3rem;
    backdrop-filter: blur(10px);
`;
const Hud = styled(HudSlots) `
    --tv-HudSlots-offset: 1rem;
    z-index: 10;
    opacity: ${({ $visible }) => ($visible ? 1 : 0)};
    transition: opacity 0.3s ease-in-out;
    pointer-events: none;
`;
const StyledBottomLeftSlot = styled(BottomLeftSlot) `
    box-sizing: border-box;
    --tv-HudSlots-offset: 0;
    --tv-HudBottomSlot-offset: 1rem;
    width: 100%;
    display: flex;
    gap: 0.5rem;
    padding: 0 1rem;
    align-items: center;
    justify-content: center;

    @media (min-width: 25rem) {
        gap: 0.75rem;
    }

    @media (orientation: landscape) {
        width: auto;
        left: 1rem;
        padding: 0;
    }
`;
const StyledBottomRightSlot = styled(BottomRightSlot) `
    --tv-HudSlots-offset: 0;
    bottom: 50%;
    right: 1rem;
    transform: translateY(50%);
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    align-items: flex-end;
    justify-content: center;

    @media (orientation: landscape) {
        bottom: 1rem;
        right: 1rem;
        transform: none;
    }
`;
